/*
  agnello's slide cart extras, built on Mode's cart drawer (sections/cart-drawer.liquid).
  Replaces Rebuy Smart Cart: subscribe-and-save switch, recommended products with quick add, discount codes.

  Every cart change goes through Mode's 'theme:cartchanged' event, which re-renders the drawer.
*/
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const cartChanged = (openDrawer = false) => {
    document.dispatchEvent(new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false, detail: { openDrawer } }));
  };

  const postJSON = (path, body) => fetch(`${root}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  }).then(async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || `HTTP ${response.status}`);
    return data;
  });

  // Switch a cart line between one-time and a selling plan (subscription).
  class AgPlanSelect extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (evt) => {
        const button = evt.target.closest('[data-plan-id]');
        if (button) this.setPlan(button.dataset.planId);
      });
      this.addEventListener('change', (evt) => {
        if (evt.target.matches('select')) this.setPlan(evt.target.value);
      });
    }

    setPlan(planId) {
      this.classList.add('is-busy');
      postJSON('cart/change.js', {
        id: this.dataset.key,
        quantity: parseInt(this.dataset.quantity, 10),
        selling_plan: planId ? parseInt(planId, 10) : null
      }).then(cartChanged).catch((err) => {
        this.classList.remove('is-busy');
        console.error('agnellos-cart: could not change subscription', err);
      });
    }
  }

  // Add a recommended product. Products with options pick their variant from the option selects.
  class AgQuickAdd extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.idInput = this.querySelector('input[name="id"]');
      this.planInput = this.querySelector('input[name="selling_plan"]');
      this.button = this.querySelector('.ag-rec__add');
      this.price = this.querySelector('[data-price]');
      this.addLabel = this.button.textContent;

      this.addEventListener('change', (evt) => {
        if (evt.target.matches('[data-option-index], [data-plan]')) this.updateVariant();
      });
      this.button.addEventListener('click', () => this.add());
      this.updateVariant();
    }

    // Current variant from the option selects (or the only variant), plus the one-time / subscribe choice if offered.
    updateVariant() {
      const selects = this.querySelectorAll('[data-option-index]');
      const chosen = Array.from(selects).map((s) => s.value);
      const variant = selects.length
        ? this.variants.find((v) => v.options.every((value, i) => value === chosen[i]))
        : this.variants[0];
      const plan = this.querySelector('[data-plan]:checked');
      const subscribing = Boolean(plan && plan.value && variant && variant.sub);
      if (this.planInput) this.planInput.value = subscribing ? plan.value : '';
      const available = Boolean(variant && variant.available);
      if (variant) {
        this.idInput.value = variant.id;
        this.price.textContent = subscribing ? variant.sub : variant.price;
        this.querySelectorAll('[data-price-once]').forEach((el) => { el.textContent = variant.price; });
        this.querySelectorAll('[data-price-sub]').forEach((el) => { el.textContent = variant.sub || variant.price; });
      }
      this.button.disabled = !available;
      this.button.textContent = available ? this.addLabel : this.dataset.soldOut;
    }

    add() {
      this.button.disabled = true;
      this.button.classList.add('btn--in-progress');
      const item = { id: parseInt(this.idInput.value, 10), quantity: 1 };
      if (this.planInput && this.planInput.value) item.selling_plan = parseInt(this.planInput.value, 10);
      postJSON('cart/add.js', { items: [item] })
        .then(() => {
          // Inside the drawer the refresh replaces this card; on a page (e.g. the homepage) reset it and open the cart.
          this.button.disabled = false;
          this.button.classList.remove('btn--in-progress');
          cartChanged(!this.closest('.cart-drawer'));
        })
        .catch((err) => {
          this.button.disabled = false;
          this.button.classList.remove('btn--in-progress');
          if (window.theme && theme.showQuickPopup) theme.showQuickPopup(err.message, $(this.button), 'error');
        });
    }
  }

  // Recommended products row: complementary products for the cart's items first,
  // then the hand-picked products already rendered in the list. Skips anything in the cart.
  class AgCartRecs extends HTMLElement {
    async connectedCallback() {
      this.list = this.querySelector('[data-ag-recs-list]');
      this.limit = parseInt(this.dataset.limit, 10) || 3;
      const inCart = (this.dataset.productIds || '').split(',').filter(Boolean);
      const picked = Array.from(this.list.querySelectorAll('.ag-rec'));
      let cards = [];

      if (this.dataset.url && inCart.length) {
        const results = await Promise.all(inCart.slice(0, 3).map((id) => this.fetchCards(id)));
        cards = results.flat();
      }

      const seen = new Set(inCart);
      const chosen = [];
      for (const card of cards.concat(picked)) {
        const id = card.dataset.productId;
        if (seen.has(id)) continue;
        seen.add(id);
        chosen.push(card);
        if (chosen.length >= this.limit) break;
      }

      this.list.replaceChildren(...chosen);
      this.hidden = chosen.length === 0;
    }

    fetchCards(productId) {
      const url = `${this.dataset.url}?product_id=${encodeURIComponent(productId)}&limit=6&intent=complementary&section_id=ag-cart-recs`;
      return fetch(url)
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => {
          const holder = document.createElement('div');
          holder.innerHTML = html;
          return Array.from(holder.querySelectorAll('.ag-rec'));
        })
        .catch(() => []);
    }
  }

  // Discount codes: apply, list, and remove, using the Ajax cart's discount field.
  class AgDiscount extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input');
      this.codesList = this.querySelector('[data-codes]');
      this.message = this.querySelector('[data-message]');
      this.codes = [];

      this.form.addEventListener('submit', (evt) => {
        evt.preventDefault();
        const code = this.input.value.trim();
        if (code) this.apply(code);
      });
      this.codesList.addEventListener('click', (evt) => {
        const button = evt.target.closest('[data-remove-code]');
        if (button) this.update(this.codes.map((c) => c.code).filter((c) => c !== button.dataset.removeCode), '');
      });

      this.boundLoad = () => this.load();
      document.addEventListener('theme:cartchanged', this.boundLoad);
      this.load();
    }

    disconnectedCallback() {
      document.removeEventListener('theme:cartchanged', this.boundLoad);
    }

    load() {
      fetch(`${root}cart.js`).then((r) => r.json()).then((cart) => this.render(cart.discount_codes || []));
    }

    apply(code) {
      const existing = this.codes.map((c) => c.code);
      if (existing.some((c) => c.toLowerCase() === code.toLowerCase())) return;
      this.update(existing.concat(code), code);
    }

    update(codes, justAdded) {
      this.classList.add('is-busy');
      postJSON('cart/update.js', { discount: codes.join(',') })
        .then((cart) => {
          this.render(cart.discount_codes || [], justAdded);
          cartChanged();
        })
        .catch(() => { this.message.textContent = this.dataset.error || "That code couldn't be applied."; })
        .finally(() => this.classList.remove('is-busy'));
    }

    render(codes, justAdded) {
      this.codes = codes;
      this.codesList.replaceChildren(...codes.map((c) => {
        const li = document.createElement('li');
        li.className = `ag-discount__code${c.applicable ? '' : ' is-invalid'}`;
        li.textContent = c.code;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.removeCode = c.code;
        remove.setAttribute('aria-label', `Remove ${c.code}`);
        remove.textContent = '×';
        li.append(remove);
        return li;
      }));

      // Plain reloads after a cart change keep whatever message is showing.
      if (justAdded === undefined) return;
      const added = justAdded && codes.find((c) => c.code.toLowerCase() === justAdded.toLowerCase());
      if (added && !added.applicable) {
        this.message.textContent = `"${added.code}" isn't valid for the items in your cart.`;
      } else {
        this.message.textContent = '';
        if (added) this.input.value = '';
      }
    }
  }

  customElements.define('ag-plan-select', AgPlanSelect);
  customElements.define('ag-quick-add', AgQuickAdd);
  customElements.define('ag-cart-recs', AgCartRecs);
  customElements.define('ag-discount', AgDiscount);
})();
